import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
  calcExportData,
  calcReportParamsSection,
  resolveReportParams
} from '@aytm/app/stats/page/question-views/info-view/components/charts/histogram/lib/report-params'
import useParamsReseed, { isUnchanged } from '@aytm/app/stats/page/question-views/components/hooks/useParamsReseed'

// Owns the histogram's bin count. Seeded from the hg<key> section and re-seeded whenever it
// changes outside the chart.
//
// The page view never parses the section: it receives an api on mount and pulls the payloads it
// needs (the section to write, the export payload) when a change is announced through
// onStateChange.
export default function useHistogramState({ params, paramsRevision, format, bars, onReady, onStateChange }) {
  const [state, setState] = useState(() => resolveReportParams({ params, format, bars }))

  const snapshot = useRef()
  snapshot.current = { state, context: { format, bars } }

  const pendingReason = useRef('seed')

  const onModelChange = useCallback(
    (change = {}) =>
      setState(current => {
        if (Object.entries(change).every(([key, value]) => current[key] === value)) return current

        pendingReason.current = 'state'

        return { ...current, ...change }
      }),
    []
  )

  const api = useMemo(
    () => ({
      getState: () => ({ ...snapshot.current.state }),
      getReportParamsSection: () => calcReportParamsSection(snapshot.current.state, snapshot.current.context),
      getExportData: () => calcExportData(snapshot.current.state),
      update: onModelChange
    }),
    [onModelChange]
  )

  const onReadyRef = useRef(onReady)
  onReadyRef.current = onReady
  useEffect(() => {
    onReadyRef.current?.(api)
  }, [api])

  useParamsReseed(params?.hg, paramsRevision, section => {
    const { format, bars } = snapshot.current.context
    const next = resolveReportParams({ params: { hg: section }, format, bars })
    setState(current => {
      if (isUnchanged(current, next)) return current
      pendingReason.current = 'seed'
      return next
    })
  })

  const onStateChangeRef = useRef(onStateChange)
  onStateChangeRef.current = onStateChange

  useEffect(() => {
    const reason = pendingReason.current
    pendingReason.current = null

    if (reason) onStateChangeRef.current?.(reason)
  }, [state])

  return { ...state, onModelChange }
}
